import {useCallback, useRef} from 'react'
import debounce from 'lodash/debounce.js'

import api from '../../common/api.js'
import {getState, updateForm, formsSelector, useAutoForm} from '../../store.js'
import {setProduct, getIsBOM, getIsKit} from '../../data/products.js'
import Icon from '../icons/Icon.js'
import TextInput from './TextInput.js'
import Zropdown, {useZropdown} from './Zropdown.js'
import cn from '../className.js'

export function getInitialForm() {
  return {
    text: '',
    results: [],
    selectedIndex: 0,
    hasFoundNothing: false,
    isLoading: false,
    serverError: null,
  }
}

export async function searchProducts(
  dropdown,
  excludedSKUs = [],
  queryParams = {},
) {
  const taskQueue = searchProducts.taskQueue || {}
  searchProducts.taskQueue = taskQueue

  taskQueue[dropdown] = {}

  try {
    const form = formsSelector(getState())[dropdown]

    if (!form) {
      return
    }

    const text = form.text.trim()

    if (!text) {
      updateForm(dropdown, {
        results: [],
        selectedIndex: 0,
        hasFoundNothing: false,
        isLoading: false,
        serverError: null,
      })

      return
    }

    updateForm(dropdown, {isLoading: true, serverError: null})

    const limit = 15 + excludedSKUs.length

    const {
      json: {product: products},
    } = await api.get('/product/', {
      limit,
      search: form.text,
      active: true,
      ...queryParams,
    })

    if (searchProducts.taskQueue[dropdown] !== taskQueue[dropdown]) {
      return
    }

    const results = products.filter(({sku}) => !excludedSKUs.includes(sku))

    updateForm(dropdown, {
      results,
      selectedIndex: 0,
      hasFoundNothing: results.length === 0,
      isLoading: false,
    })
  } catch (err) {
    updateForm(dropdown, {
      results: [],
      selectedIndex: 0,
      serverError: err.message,
      isLoading: false,
    })
  } finally {
    if (searchProducts.taskQueue[dropdown] === taskQueue[dropdown]) {
      delete taskQueue[dropdown]
    }
  }
}

/**
 * A product search input
 *
 * @param {Object} props
 * @param {String} [props.className]
 * @param {String|React.ReactNode} [props.label]
 * @param {String} [props.placeholder]
 * @param {String} props.dropdown
 * @param {Function} props.onSelect
 * @param {String[]} [props.excludedSKUs]
 * @param {Object} [props.queryParams] Filters to limit search results
 * @param {Boolean} [props.autoFocus]
 * @param {Function} [props.onBlur]
 * @returns {React.ReactNode}
 */
export default function ProductFilter({
  className,
  label,
  placeholder,
  dropdown,
  onSelect,
  excludedSKUs,
  queryParams,
  autoFocus,
  onBlur,
}) {
  const id = `${dropdown}__input`
  placeholder = placeholder || 'Search for a product...'

  const debouncedSearchProducts = useCallback(
    debounce(() => searchProducts(dropdown, excludedSKUs, queryParams), 400),
    [dropdown, excludedSKUs, queryParams],
  )

  const ref = useRef()

  const form = useAutoForm(dropdown, getInitialForm())

  const {onChangeInternal, onSelectInternal, onConfirm, onUp, onDown} =
    useZropdown({
      dropdown,
      form,
      onChange: () => debouncedSearchProducts(),
      onSelect: (product) => {
        setProduct(product)

        onSelect(product)

        onChangeInternal('')
      },
    })

  return (
    <div className={cn`outerwrap--product-search-zropdown ${className}`}>
      {label && (
        <label className="label--product-search" htmlFor={id}>
          {label}
        </label>
      )}
      <div className="wrap--product-search" ref={ref}>
        <label className="label--input-prefix label--search" htmlFor={id}>
          <Icon name="search" className="icon--input-prefix" />
          <TextInput
            className="input--search input--lighter inline-block v-align-middle"
            id={id}
            value={form.text}
            placeholder={placeholder}
            onChange={(value) => onChangeInternal(value)}
            onBlur={(event) => onBlur && onBlur(event)}
            autoFocus={autoFocus}
          />
        </label>
      </div>
      <Zropdown
        parentRef={ref}
        dropdown={dropdown}
        form={form}
        onConfirm={onConfirm}
        onUp={onUp}
        onDown={onDown}
      >
        {form.isLoading && (
          <li className="list__item--dropdown list__item--dropdown-empty align-center">
            <span className="spinner--sm v-align-middle" />
            <strong className="inline-block v-align-middle fs-00 op-75 margin-left-5">
              Loading...
            </strong>
          </li>
        )}
        {form.results.map((product, index) => (
          <li key={product.sku} className="list__item list__item--dropdown">
            <button
              key={product.id}
              className={cn`btn--link list__link list__link--dropdown flex--justify-nowrap ${{
                'list__link--dropdown-arrow-focus':
                  index === form.selectedIndex,
              }}`}
              onClick={() => onSelectInternal(product)}
            >
              <div>
                <strong className="block">{product.name}</strong>
                <span className="block unbold">SKU: {product.sku}</span>
              </div>
              {getIsKit(product) && (
                <div>
                  <div className="label__callout label__callout--grey inline-block margin-right-5 fs-n2">
                    <strong>Kit</strong>
                  </div>
                </div>
              )}
              {getIsBOM(product) && (
                <div>
                  <div className="label__callout label__callout--grey inline-block margin-right-5 fs-n2">
                    <strong>BOM</strong>
                  </div>
                </div>
              )}
            </button>
          </li>
        ))}
        {form.hasFoundNothing && (
          <li className="list__item--dropdown list__item--dropdown-empty align-center">
            <em>No products were found</em>
          </li>
        )}
      </Zropdown>
    </div>
  )
}
